import { Component } from 'preact';
import { createPortal } from 'preact/compat';
import { subscribeHoveredCard } from '../../utils/game/cardZoomBus';
import FoilCanvas from '../FoilCanvas';
import { resolveLocalImageUrl } from '../../utils/localApi';
import { isFoilFinish } from '../../utils/sorcery/foil.js';
import { foilZonesForCard } from '../../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams.js';
import { RADIUS } from '../../lib/medievalTheme';

// TTS-style hold-Alt zoom. While the local player holds Alt/Option
// AND the cursor is over a card (board or hand), render a large
// floating preview centered on the canvas so the user can read the
// card without opening the full inspector.
//
// Self-contained: subscribes to cardZoomBus for the live hovered card
// and listens for window key events directly. GameBoard only needs
// to mount this once and feed hovers into the bus. Adding new hover
// surfaces (controller aim, replay UI) just means more bus producers,
// not edits here.
//
// Anti-cheat: face-down cards are filtered here, never broadcast as
// "no zoom"; the bus still carries the instance so other consumers
// can react to face-down hovers.
//
// Portaled into `.app-canvas` so position:fixed letterboxes with the
// canvas instead of escaping to the viewport bars (same pattern as
// CardHoverPreview / SkillTree tooltips).

const PORTRAIT_WIDTH_CQW = 28;
const PORTRAIT_HEIGHT_CQW = PORTRAIT_WIDTH_CQW * (88 / 63);
const LANDSCAPE_WIDTH_CQW = PORTRAIT_HEIGHT_CQW;
const LANDSCAPE_HEIGHT_CQW = PORTRAIT_WIDTH_CQW;

export default class CardAltZoomOverlay extends Component {
  state = {
    altHeld: false,
    card: null,
  };

  componentDidMount() {
    this._unsubscribe = subscribeHoveredCard((card) => {
      this.setState({ card });
    });
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.handleBlur);
  }

  componentWillUnmount() {
    if (this._unsubscribe) this._unsubscribe();
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleBlur);
  }

  handleKeyDown = (e) => {
    if (e.key === 'Alt' && !this.state.altHeld) {
      this.setState({ altHeld: true });
    }
  };

  handleKeyUp = (e) => {
    // Some browsers fire keyup with key='Alt' even when altKey is
    // already false; either way, releasing the modifier ends the zoom.
    if (e.key === 'Alt' || !e.altKey) {
      if (this.state.altHeld) this.setState({ altHeld: false });
    }
  };

  // Window blur (focus switch, OS-level shortcut, etc.) can swallow
  // the keyup. Clear the held state so the overlay doesn't get stuck.
  handleBlur = () => {
    if (this.state.altHeld) this.setState({ altHeld: false });
  };

  render() {
    const { altHeld, card } = this.state;
    if (!altHeld || !card) return null;
    if (card.faceDown) return null;
    if (!card.imageUrl) return null;
    const imageUrl = resolveLocalImageUrl(card.imageUrl);
    const isFoil = isFoilFinish(card.foiling);

    const isSite = !!(card.isSite || card.played_horizontally);
    const widthCqw = isSite ? LANDSCAPE_WIDTH_CQW : PORTRAIT_WIDTH_CQW;
    const heightCqw = isSite ? LANDSCAPE_HEIGHT_CQW : PORTRAIT_HEIGHT_CQW;

    const frameStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      width: `${widthCqw}cqw`,
      height: `${heightCqw}cqw`,
      transform: 'translate(-50%, -50%)',
      zIndex: 280,
      pointerEvents: 'none',
      borderRadius: isSite ? RADIUS.cardLandscape : RADIUS.card,
      overflow: 'hidden',
      boxShadow: '0 36px 72px rgba(0,0,0,0.85), 0 12px 24px rgba(0,0,0,0.6)',
    };

    // Sites store art portrait but are played landscape. Render the
    // portrait image rotated 90° inside the landscape frame, mirroring
    // CardHoverPreview / the deck-builder sidebar.
    const node = isSite ? (
      <div style={frameStyle}>
        <img
          src={imageUrl}
          alt={card.name || ''}
          draggable={false}
          style={{
            position: 'absolute',
            width: `${heightCqw}cqw`,
            height: `${widthCqw}cqw`,
            top: `calc((${heightCqw}cqw - ${widthCqw}cqw) / 2)`,
            left: `calc((${widthCqw}cqw - ${heightCqw}cqw) / 2)`,
            transform: 'rotate(90deg)',
            transformOrigin: 'center center',
            objectFit: 'cover',
          }}
        />
        {isFoil ? (
          <FoilCanvas imageUrl={imageUrl} zones={foilZonesForCard(card)} params={DEFAULT_FOIL_PARAMS} scrollSweep={false} rotate90 />
        ) : null}
      </div>
    ) : (
      <div style={frameStyle}>
        <img
          src={imageUrl}
          alt={card.name || ''}
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {isFoil ? (
          <FoilCanvas imageUrl={imageUrl} zones={foilZonesForCard(card)} params={DEFAULT_FOIL_PARAMS} scrollSweep={false} />
        ) : null}
      </div>
    );

    const canvas = typeof document !== 'undefined'
      ? document.querySelector('.app-canvas')
      : null;
    return canvas ? createPortal(node, canvas) : node;
  }
}
